import { Link } from "react-router-dom";
import { MapPin, ExternalLink, Target } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Parse an ISO date (YYYY-MM-DD) without timezone shifting. */
export function isoToDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function fmtDay(iso, opts = {}) {
  const d = isoToDate(iso);
  if (!d) return "";
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric", ...opts });
}

/** "Fri 18 – Sun 20 Sep 2026" / "Fri 18 Sep 2026" / "Date to be confirmed". */
export function fmtRange(start, end) {
  const s = isoToDate(start), e = isoToDate(end);
  if (!s && !e) return "Date not published";
  if (!e || !s || start === end) return fmtDay(start || end);
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${s.toLocaleDateString("en-AU", { weekday: "short", day: "numeric" })} – ${fmtDay(end)}`;
  }
  return `${fmtDay(start)} – ${fmtDay(end)}`;
}

/** '18–20 Sep 2026' / '30 Sep – 2 Oct 2026' / '18 Sep 2026' — compact form for <title>. */
export function fmtRangeShort(start, end) {
  const s = isoToDate(start) || isoToDate(end), e = isoToDate(end) || isoToDate(start);
  if (!s || !e) return "";
  const dm = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  const dmy = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  if (s.getTime() === e.getTime()) return dmy(s);
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) return `${s.getDate()}–${dmy(e)}`;
  return `${dm(s)} – ${dmy(e)}`;
}

/** Venue plus state name, unless the venue already says it. */
export function eventPlace(ev) {
  if (ev.is_postal) return "Postal event";
  const venue = ev.venue || "";
  const sn = ev.state_name || "";
  const lower = venue.toLowerCase();
  if (sn && !lower.includes(sn.toLowerCase()) && !lower.split(/\W+/).includes((ev.state || "").toLowerCase())) return venue ? `${venue}, ${sn}` : sn;
  return venue;
}

export const FLAG_LABEL = { cancelled: "Cancelled", postponed: "Postponed" };

/** Photon CDN URL with a width hint; falls back to the original image. */
export function eventImage(ev, width = 800) {
  if (!ev) return null;
  if (ev.image) return `${ev.image}&w=${width}`;
  return ev.image_original || null;
}

export function EventCard({ event, compact = false }) {
  const start = isoToDate(event.start_date || event.end_date);
  const img = eventImage(event, 800);
  const where = event.is_postal ? "Postal event" : (event.venue_short || event.venue || "Venue on SSAA event page");
  return (
    <article
      className="group relative flex flex-col bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-slate-400 hover:shadow-md transition-all"
      data-testid={`event-card-${event.slug}`}
    >
      <Link to={event.path} className="block aspect-[16/9] bg-slate-900 overflow-hidden relative" aria-label={event.title}>
        {img ? (
          <img
            src={img}
            alt={event.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
            onError={(e) => {
              if (event.image_original && e.currentTarget.src !== event.image_original) e.currentTarget.src = event.image_original;
              else e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Target className="h-10 w-10 text-white/20" strokeWidth={1.2} />
          </div>
        )}
        {start && (
          <div className="absolute top-3 left-3 bg-white rounded-sm px-2.5 py-1.5 text-center shadow-sm min-w-[52px]" aria-hidden="true">
            <div className="text-[10px] font-bold uppercase tracking-wider text-orange-600 leading-none">{MONTHS[start.getMonth()]}</div>
            <div className="font-heading text-xl font-extrabold text-slate-900 leading-none mt-0.5 num">{start.getDate()}</div>
          </div>
        )}
        {event.flag ? (
          <span className="absolute top-3 right-3 bg-red-700 text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-sm">{FLAG_LABEL[event.flag]}</span>
        ) : event.status === "past" && (
          <span className="absolute top-3 right-3 bg-slate-900/80 text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-sm">Ended</span>
        )}
      </Link>
      <div className={`flex flex-col flex-1 ${compact ? "p-4" : "p-5"}`}>
        {event.discipline && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 mb-1.5 truncate">{event.discipline}</p>
        )}
        <h3 className="font-heading text-base md:text-[17px] font-bold text-slate-900 leading-snug line-clamp-2 mb-2">
          <Link to={event.path} className="hover:text-orange-600 transition-colors" data-testid="event-card-link">{event.title}</Link>
        </h3>
        <p className="text-sm text-slate-600 mb-1">{fmtRange(event.start_date, event.end_date)}</p>
        <p className="text-sm text-slate-500 flex items-start gap-1.5 line-clamp-1">
          <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
          <span className="truncate">{where}</span>
        </p>
        <div className="mt-auto pt-4 flex items-center justify-between gap-2">
          {event.state ? (
            <Badge variant="outline" className="text-slate-700 border-slate-300 font-semibold">{event.state}</Badge>
          ) : <span />}
          <a
            href={event.source_url}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-orange-600 transition-colors"
            data-testid="event-card-ssaa-link"
          >
            SSAA <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </div>
      </div>
    </article>
  );
}

export function EventCardSkeleton() {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden animate-pulse" aria-hidden="true">
      <div className="aspect-[16/9] bg-slate-100" />
      <div className="p-5 space-y-3">
        <div className="h-2.5 w-20 bg-slate-100 rounded" />
        <div className="h-4 w-3/4 bg-slate-100 rounded" />
        <div className="h-3 w-1/2 bg-slate-100 rounded" />
        <div className="h-3 w-2/3 bg-slate-100 rounded" />
      </div>
    </div>
  );
}
